import React from 'react';
import { Button, Divider, Heading, IconButton, Text } from '@wix/design-system';
import { DismissSmall, StatusCompleteFilledSmall } from '@wix/wix-ui-icons-common';
import { useTranslation } from '@wix/wix-i18n-config';
import { postPurchaseBi } from '../postPurchaseBi';
import type { StepItem } from '../types';
import { DomainRow } from './DomainRow';
import styles from '../PostPurchaseStep.scss';

interface StepCardProps {
  step: StepItem;
  isSelected: boolean;
  inlinePanel?: React.ReactNode;
  isPanelOpen: boolean;
  cardRef?: React.Ref<HTMLElement>;
  domainValue: string;
  metaSiteId: string | null;
  suggestedDomain: string;
  showInlineDetails: boolean;
  onDomainValueChange: (value: string) => void;
  onSelect: () => void;
  onAction: (event: React.MouseEvent) => void;
  onClosePanel: () => void;
}

export function StepCard({
  step,
  isSelected,
  inlinePanel,
  isPanelOpen,
  cardRef,
  domainValue,
  metaSiteId,
  suggestedDomain,
  showInlineDetails,
  onDomainValueChange,
  onSelect,
  onAction,
  onClosePanel,
}: StepCardProps) {
  const { t } = useTranslation();
  const classNames = [
    styles.postPurchaseStep,
    isSelected ? styles.postPurchaseStepSelected : '',
    step.done ? styles.postPurchaseStepDone : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <article
      ref={cardRef}
      className={classNames}
      onClick={onSelect}
      aria-pressed={isSelected}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect();
        }
      }}
    >
      <div className={styles.postPurchaseStepNumber} aria-hidden="true">
        {step.done ? <StatusCompleteFilledSmall /> : <span>{step.number}</span>}
      </div>
      <div className={styles.postPurchaseStepBody}>
        <Heading as="h2" size="small">
          {step.title}
        </Heading>
        <Text size="small" secondary>
          {step.description}
        </Text>
      </div>
      {step.done && (
        <Text size="small" weight="bold" skin="success" className={styles.postPurchaseStepDoneLabel}>
          <StatusCompleteFilledSmall />
          {t('post_purchase.done_label')}
        </Text>
      )}
      {isPanelOpen ? (
        <IconButton
          priority="secondary"
          size="medium"
          onClick={(event) => {
            event.stopPropagation();
            onClosePanel();
          }}
          aria-label={t('post_purchase.step.close_aria', { stepTitle: step.title })}
        >
          <DismissSmall />
        </IconButton>
      ) : (
        !step.done && (
          <Button priority="primary" size="medium" onClick={onAction}>
            {t('post_purchase.cta.go')}
          </Button>
        )
      )}
      {step.id === 'domain' && isSelected && showInlineDetails && !step.done && (
        <div
          className={styles.postPurchaseStepExpand}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          <Divider />
          <DomainRow
            value={domainValue}
            onChange={onDomainValueChange}
            metaSiteId={metaSiteId}
            buttonLabel={step.cta}
            placeholder={suggestedDomain}
            onConnectClick={postPurchaseBi.connectDomain}
          />
        </div>
      )}
      {inlinePanel && (
        <div
          className={styles.postPurchaseStepDrawer}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          {inlinePanel}
        </div>
      )}
    </article>
  );
}
